import React, { useEffect, useState, useMemo } from "react";
import { api, formatBRL } from "../lib/api";
import { Loader2, Layers, FileText, Handshake, TrendingUp, AlertTriangle, DollarSign, Wallet, Filter, Users, Clock, CheckCircle2, Percent } from "lucide-react";

function Kpi({ label, value, icon: Icon, accent, testid, suffix }) {
  return (
    <div className="fc-card p-4 fc-card-hover" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-[#A3A3A3] font-medium">{label}</div>
        {Icon && (
          <div className={`w-7 h-7 rounded-md flex items-center justify-center border ${accent || "border-[#262626] text-[#A3A3A3]"}`}>
            <Icon size={13} strokeWidth={1.8}/>
          </div>
        )}
      </div>
      <div className="fc-kpi-value text-xl sm:text-[22px] mt-2 leading-none">
        {value}{suffix && <span className="text-[12px] text-[#737373] ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function InstallmentsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ socio_id: "", indicator_id: "", client_id: "", kind: "", frequency: "" });
  const [socios, setSocios] = useState([]);
  const [indicators, setIndicators] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, i] = await Promise.all([api.get("/socios"), api.get("/indicators")]);
        setSocios(Array.isArray(s.data) ? s.data : []);
        setIndicators(Array.isArray(i.data) ? i.data : []);
      } catch (_) {
        /* noop */
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const { data } = await api.get("/dashboard/installments", { params });
      setData(data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <div data-testid="installments-dashboard">
      {/* Filtros */}
      <div className="fc-card p-4 mb-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#737373] mb-3">
          <Filter size={11}/> Filtros
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          <select className="fc-input" value={filters.kind} onChange={(e) => setF("kind", e.target.value)} data-testid="filter-kind">
            <option value="">Todos os tipos</option>
            <option value="parcelado">Parcelado</option>
            <option value="acordo">Acordo</option>
          </select>
          <select className="fc-input" value={filters.frequency} onChange={(e) => setF("frequency", e.target.value)} data-testid="filter-frequency">
            <option value="">Todas frequências</option>
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
          </select>
          <select className="fc-input" value={filters.socio_id} onChange={(e) => setF("socio_id", e.target.value)} data-testid="filter-socio">
            <option value="">Todos sócios</option>
            {socios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="fc-input" value={filters.indicator_id} onChange={(e) => setF("indicator_id", e.target.value)} data-testid="filter-indicator">
            <option value="">Todos indicadores</option>
            {indicators.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <input
            type="text"
            className="fc-input"
            placeholder="ID do cliente"
            value={filters.client_id}
            onChange={(e) => setF("client_id", e.target.value)}
            data-testid="filter-client"
          />
        </div>
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4,5,6,7,8].map(i=><div key={i} className="fc-card h-24 shimmer"/>)}</div>
      ) : data && (
        <>
          {/* Capital */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2">Capital</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Kpi label="Parcelado ativo" value={formatBRL(data.capital_parcelado_ativo)} icon={FileText} accent="border-blue-500/40 text-blue-300" testid="kpi-cap-parcelado"/>
            <Kpi label="Em acordos" value={formatBRL(data.capital_acordos_ativos)} icon={Handshake} accent="border-violet-500/40 text-violet-300" testid="kpi-cap-acordos"/>
            <Kpi label="Saldo a receber" value={formatBRL(data.saldo_total_a_receber)} icon={Wallet} accent="border-[#D4AF37]/40 text-[#D4AF37]" testid="kpi-saldo-receber"/>
            <Kpi label="Capital recuperado" value={formatBRL(data.capital_recuperado)} icon={CheckCircle2} accent="border-emerald-500/40 text-emerald-300" testid="kpi-cap-recuperado"/>
          </div>

          {/* Lucro */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2">Lucro</div>
          <div className="grid grid-cols-2 lg:grid-cols-2 gap-3 mb-5">
            <Kpi label="Lucro recebido" value={formatBRL(data.lucro_recebido)} icon={TrendingUp} accent="border-emerald-500/40 text-emerald-300" testid="kpi-lucro-recebido"/>
            <Kpi label="Lucro pendente" value={formatBRL(data.lucro_pendente)} icon={Clock} accent="border-amber-500/40 text-amber-300" testid="kpi-lucro-pendente"/>
          </div>

          {/* Operacional */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2">Operacional</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Contratos ativos" value={data.contratos_ativos} icon={Layers} testid="kpi-contratos-ativos"/>
            <Kpi label="Parcelas a vencer" value={data.parcelas_a_vencer} icon={Clock} testid="kpi-parc-vencer"/>
            <Kpi label="Parcelas vencidas" value={data.parcelas_vencidas} icon={AlertTriangle} accent="border-red-500/40 text-red-300" testid="kpi-parc-vencidas"/>
            <Kpi label="Clientes inadimplentes" value={data.clientes_inadimplentes} icon={Users} accent="border-red-500/30 text-red-300" testid="kpi-clientes-inad"/>
          </div>
          <div className="mt-3">
            <Kpi label="Taxa de inadimplência" value={`${data.taxa_inadimplencia.toFixed(2)}%`} icon={Percent} accent="border-yellow-500/40 text-yellow-300" testid="kpi-taxa-inad"/>
          </div>
        </>
      )}
    </div>
  );
}
